import { max, min, noop, set } from 'lodash/fp';
import Slider from 'rc-slider';
import React, { useCallback, useMemo } from 'react';

import styles from './RiskTolerance.module.scss';
import { riskAssociatedColors } from '../../admin/risk-tolerance/utils';
import { RiskAssociated } from '../../admin/risk-tolerance/store';

const { fadedGrey, fixedWhite } = styles;

interface Mark {
    label: string;
    key: number;
}

interface RiskToleranceSliderProps {
    value: number;
    setValue: (value: number) => void;
    disabled: boolean;
    marks?: Mark[];
    shouldAdjustValueForRisk?: boolean;
    renderLeft?: boolean;
}

interface MarkerProps {
    label: string;
    centreText?: boolean;
    renderLeft?: boolean;
}

const Marker: React.FC<MarkerProps> = ({ label, centreText = false, renderLeft = false }) => {
    const style: React.CSSProperties = useMemo(() => {
        const width = centreText ? '150px' : '100px';
        if (centreText) {
            return { width, textAlign: 'center' };
        }
        if (renderLeft) {
            return { width, textAlign: 'right', transform: `translateX(-${parseInt(width) + 30}px)` };
        }
        return { width, textAlign: 'left' };
    }, [renderLeft, centreText]);
    return (
        <div className={styles.markerWrapper} style={style}>{label}</div>
    );
};

const weightingMarks = [
    { key: 1, label: 'Of Little Importance' },
    { key: 2, label: '-' },
    { key: 3, label: 'Important' },
    { key: 4, label: '-' },
    { key: 5, label: 'Very Important' }
];

const getMarks = (marks = weightingMarks, centreText = false, renderLeft = false) => marks.reduce((acc, { key, label }) => set(key, <Marker label={label} centreText={centreText} renderLeft={renderLeft} />, acc), {});

const getLinearGradient = ((weighting: number, getSelectedColor: (value: number) => string) => {
    const weightingArray = Array(weighting).fill(1).map((value, index) => value * index + 1);
    const percentageSplit = 100 / weightingArray.length;
    return weightingArray.map(value => `${getSelectedColor(value)} ${percentageSplit * value}%`).join(', ');
});

export const RiskToleranceSlider: React.FC<RiskToleranceSliderProps> = ({
    value,
    setValue,
    disabled,
    marks = weightingMarks,
    shouldAdjustValueForRisk = false,
    renderLeft = false
}) => {
    const adjustment = useMemo(() => shouldAdjustValueForRisk ? 1 : 0, [shouldAdjustValueForRisk]);
    const getSelectedColor = useCallback((value: number) => riskAssociatedColors[(value - adjustment) as RiskAssociated], [adjustment]);
    const selectedColor = useMemo(() => getSelectedColor(value), [value, getSelectedColor]);
    const linearGradient = getLinearGradient(value, getSelectedColor);
    const minValue = useMemo(() => min(marks.map(({ key }) => key))!, [marks]);
    const maxValue = useMemo(() => max(marks.map(({ key }) => key))!, [marks]);
    const defaultValue = useMemo(() => (minValue + maxValue) / 2, [minValue, maxValue]);

    return (
        <div className={styles.sliderWrapper} style={{ justifyContent: renderLeft ? 'flex-end' : 'flex-start' }}>
            <Slider
                min={minValue}
                onChange={value => setValue(value as number)}
                defaultValue={defaultValue}
                value={value}
                max={maxValue}
                step={1}
                vertical
                marks={getMarks(marks, false, renderLeft)}
                railStyle={{
                    backgroundColor: fadedGrey,
                    width: '6px'
                }}
                handleStyle={{
                    backgroundColor: selectedColor,
                    border: `1px solid ${selectedColor}`,
                    height: '16px',
                    width: '16px',
                    marginLeft: '-5px',
                    opacity: 1
                }}
                dotStyle={(dotValue: number) => ({
                    backgroundColor: fixedWhite,
                    border: `2px solid ${getSelectedColor(dotValue)}`,
                    height: '12px',
                    width: '12px',
                    left: '-1px'
                })}
                trackStyle={{ backgroundImage: `linear-gradient(to top, ${linearGradient}`, width: '6px' }}
                disabled={disabled}
            />
        </div>
    );
};

interface HorizontalWeightingSliderProps {
    weighting: number;
}

export const HorizontalWeightingSlider: React.FC<HorizontalWeightingSliderProps> = ({ weighting }) => {
    const getSelectedColor = useCallback((value: number) => riskAssociatedColors[(value - 1) as RiskAssociated], []);
    const selectedColor = useMemo(() => getSelectedColor(weighting), [weighting, getSelectedColor]);
    const linearGradient = getLinearGradient(weighting, getSelectedColor);

    return (
        <div className={styles.horizontalWeightingWrapper}>
            <div className={styles.horizontalSliderWrapper}>
                <Slider
                    min={1}
                    marks={getMarks(weightingMarks, true)}
                    onChange={noop}
                    value={weighting}
                    max={5}
                    step={1}
                    trackStyle={{
                        backgroundImage: `linear-gradient(to right, ${linearGradient}`,
                        height: '6px'
                    }}
                    railStyle={{
                        backgroundColor: fadedGrey
                    }}
                    handleStyle={{
                        backgroundColor: selectedColor,
                        border: `1px solid ${selectedColor}`,
                        height: '16px',
                        width: '16px',
                        marginTop: '-6px',
                        opacity: 1,
                        cursor: 'default'
                    }}
                    dotStyle={(dotValue: number) => ({
                        backgroundColor: fixedWhite,
                        border: `1px solid ${getSelectedColor(dotValue)}`,
                        cursor: 'default'
                    })}
                />
            </div>
        </div>
    );
};
